import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { type Params } from '@angular/router';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type SiteEntity } from '@common/types/site.type';
import { type SitePageEntity } from '@common/types/site-page.type';
import { type Domain } from '@common/types/domain.type';

import { PagesService } from '@web/store/website/pages/pages.service';

import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { PagesDrawerItemComponent } from './pages-drawer-item/pages-drawer-item.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'web-pages-drawer-list',
    templateUrl: './pages-drawer-list.component.html',
    styleUrls: ['./pages-drawer-list.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, NgFor, PagesDrawerItemComponent, AsyncPipe, TranslateModule],
})
export class PagesDrawerListComponent {
    private readonly pagesService: PagesService = inject(PagesService);

    private readonly routerFacade: RouterFacade = inject(RouterFacade);
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);
    private readonly domainsFacade: DomainsFacade = inject(DomainsFacade);

    private page: number = 1;
    private perPage: number = 100;

    public readonly site$: Observable<SiteEntity> = this.siteFacade.site$;
    public readonly domains$: Observable<Domain[]> = this.domainsFacade.domains$;
    public readonly isSaving$: Observable<boolean> = this.siteFacade.isSaving$;
    public readonly routerParams$: Observable<Params> = this.routerFacade.routerParams$;
    public readonly selectTotal$: Observable<number> = this.pagesFacade.selectTotal$;

    public readonly pages$: Observable<SitePageEntity[]> = this.pagesFacade.pages$.pipe(
        map((pages) => pages.filter((page) => !this.pagesService.shouldHidePageInConstructor(page)))
    );

    public getMorePages() {
        this.page++;
        this.pagesFacade.loadMorePages(this.page, this.perPage);
    }

    public isAllPagesLoaded(total: number) {
        return total > this.page * this.perPage;
    }
}
